import type { AssistantBindingContextValue } from "../../react/providers/AssistantBindingProvider";

// Greeting builder for the assistant panel.
//
// Three tiers in increasing specificity (use the most specific that fits):
//
//   1. RECORD-SCOPED — binding has recordRef + recordDisplayName.
//      Highest-signal context the assistant has: "I see you're looking
//      at <display_name>". Operators recognize records by name, not
//      screen name; this is the most useful greeting.
//
//   2. ACTION-SCOPED — binding has model + action but no record (list /
//      kanban / new-record draft). "I see you're on the <Action> screen."
//
//   3. GLOBAL — no binding. Generic "I can help with anything" greeting.

const GLOBAL_GREETINGS = [
    "Hi! I can help you find records, summarize data, or navigate the system. What would you like to do?",
    "Hello! Ask me anything about what you're looking at — or use a slash command for shortcuts.",
    "Hey! I'm your read-only co-pilot. Try asking me to filter, sort, or group the current view.",
];

function pickGlobal(seed: string | undefined): string {
    if (!seed) return GLOBAL_GREETINGS[0];
    const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    let h = 0;
    for (const ch of seed) h = ((h << 5) - h + ch.charCodeAt(0)) | 0;
    const idx = Math.abs(h + day) % GLOBAL_GREETINGS.length;
    return GLOBAL_GREETINGS[idx];
}

/** Convert a model key like "crm.quote.line" into a human-readable label
    "Crm Quote Line" → "Quote Line" by stripping the prefix segment. Falls
    back to the raw key for unknown prefixes. */
export function humanizeModelKey(modelKey: string): string {
    const parts = modelKey.split(".");
    // Drop common module prefix ("crm", "res", "logistics", "foundation")
    // to keep labels concise; capitalise remaining segments.
    const dropFirst = parts.length > 1 && ["crm", "res", "logistics", "foundation", "ir"].includes(parts[0])
        ? parts.slice(1)
        : parts;
    return dropFirst
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
}

export function buildGreeting(
    seed: string | undefined,
    binding?: AssistantBindingContextValue,
): string {
    if (!binding || !binding.anchoredModelKey) {
        return pickGlobal(seed);
    }

    // Tier 1 — record-scoped
    if (binding.recordRef && binding.recordDisplayName) {
        const modelLabel = humanizeModelKey(binding.anchoredModelKey).toLowerCase();
        return `I see you're looking at **${binding.recordDisplayName}**. I can summarize this ${modelLabel}, find related records, or answer questions about it. What would you like to know?`;
    }

    // Tier 2 — action-scoped (list / kanban / new-record draft)
    const actionLabel = humanizeModelKey(binding.anchoredModelKey);
    return `I see you're on the ${actionLabel} screen. Want me to filter, sort, or group these records?`;
}
